import {
  Button,
  CircularProgress,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormHelperText,
  Grid,
  TextField,
  Theme,
  Tooltip,
} from "@mui/material";
import { makeStyles } from "helpers/StyleHelper";
import { useTheme } from "@mui/material/styles";
import Alert from "@mui/material/Alert";
import Field from "components/Field";
import FieldRichText from "components/FieldRichText";
import { FORM_ERROR } from "final-form";
import arrayMutators from "final-form-arrays";
import useGlobalContext from "hooks/globalData";
import { pick } from "lodash";
import { useCallback, useMemo, useState } from "react";
import { FormProps, withTypes } from "react-final-form";
import { useIntl } from "react-intl";
import {
  IQualification,
  OrganizationQualificationRequest,
} from "services/KnowfullyAdminClient";
import translateError from "utils/translateError";
import ContextOrganizationField from "components/ContextOrganizationField";
import SelectQualification from "pages/SkusPage/SkuQualifications/SelectQualification";
import SelectQualificationType from "./SelectQualificationType";

const useStyles = makeStyles((theme: Theme) => ({
  feedback: {
    width: "100%",
    margin: "20px 0 20px 0",
  },
}));

const { Form } = withTypes<OrganizationQualificationRequest>();

type QualificationFormProps = {
  qualification?: IQualification;
  title: string;
  onCancel?: () => void;
  onSubmit: () => void;
};

const QualificationForm = ({
  qualification,
  title,
  onCancel,
  onSubmit,
}: QualificationFormProps & FormProps<OrganizationQualificationRequest>) => {
  const { formatMessage: t } = useIntl();
  const styles = useStyles(useTheme());
  const { qualificationService } = useGlobalContext();
  const isEditing = !!qualification;
  const createdByCrm = qualification?.createdByCrm ?? false;

  const [
    selectedBaseQualification,
    setSelectedBaseQualification,
  ] = useState<IQualification | null>(null);

  const init = useMemo(
    () =>
      isEditing
        ? {
            id: qualification?.organizationQualificationId,
            qualificationId: qualification?.id,
            ...pick(qualification ?? {}, [
              "value",
              "acronym",
              "displayValue",
              "displayOrder",
              "qualificationTypeId",
            ]),
          }
        : {},
    [qualification, isEditing],
  );

  const handleSubmit = useCallback(
    async (values: OrganizationQualificationRequest) => {
      try {
        if (selectedBaseQualification) {
          values.qualificationId = selectedBaseQualification.id;
        }
        await qualificationService.saveQualification(values);
        onSubmit();
      } catch (error) {
        console.log(error);
        return { [FORM_ERROR]: translateError(error, t) };
      }
    },
    [onSubmit, qualificationService, selectedBaseQualification, t],
  );

  return (
    <Form
      onSubmit={handleSubmit}
      initialValues={init}
      mutators={{ ...arrayMutators }}
      render={({ handleSubmit, submitting, submitError, form, values }) => (
        <form onSubmit={handleSubmit}>
          <DialogTitle id="form-dialog-title">{title}</DialogTitle>
          <DialogContent dividers>
            <Grid container spacing={2}>
              <ContextOrganizationField
                values={values}
                formChange={form.change}
              />
              {isEditing ? (
                <Grid item xs={12}>
                  <Tooltip
                    title={
                      createdByCrm
                        ? t({
                            id: "qualificationFormCreatedInCrmValueMessage",
                          })
                        : ""
                    }
                  >
                    <TextField
                      variant="standard"
                      fullWidth
                      disabled
                      label={t({ id: "valueLabel" })}
                      value={qualification?.value ?? ""}
                    />
                  </Tooltip>
                </Grid>
              ) : (
                <>
                  {values.organizationId && (
                    <Grid item xs={12}>
                      <SelectQualification
                        organizationId={values.organizationId}
                        useBaseTable
                        onChange={(qual) => {
                          setSelectedBaseQualification(qual);
                          form.change("value", qual?.value ?? "");
                          form.change("acronym", qual?.acronym ?? "");
                          form.change("displayValue", qual?.displayValue ?? "");
                          form.change("displayOrder", qual?.displayOrder);
                          form.change(
                            "qualificationTypeId",
                            qual?.qualificationTypeId ?? "",
                          );
                        }}
                        label={t({ id: "selectBaseAccreditationBodyLabel" })}
                      />
                    </Grid>
                  )}
                  <Field name="value" required>
                    {({ input, meta }) => (
                      <Grid item xs={12}>
                        <TextField
                          variant="standard"
                          fullWidth
                          disabled={!!selectedBaseQualification}
                          label={t({ id: "valueLabel" })}
                          InputLabelProps={{ required: true }}
                          error={meta.error && meta.submitFailed}
                          helperText={
                            meta.error && meta.submitFailed && meta.error
                          }
                          {...input}
                        />
                      </Grid>
                    )}
                  </Field>
                </>
              )}

              <Field name="displayValue">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FieldRichText
                      input={input}
                      meta={meta}
                      label={t({
                        id: "accreditationBodyFormDisplayValueLabel",
                      })}
                      init={{ statusbar: false }}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="acronym">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "acronymLabel" })}
                      error={meta.error && meta.submitFailed}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="displayOrder">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <TextField
                      variant="standard"
                      fullWidth
                      label={t({ id: "displayOrderLabel" })}
                      error={meta.error && meta.submitFailed}
                      InputProps={{ type: "number" }}
                      helperText={meta.error && meta.submitFailed && meta.error}
                      {...input}
                    />
                  </Grid>
                )}
              </Field>

              <Field name="qualificationTypeId">
                {({ input, meta }) => (
                  <Grid item xs={12}>
                    <FormControl
                      fullWidth
                      error={meta.error && meta.submitFailed}
                    >
                      <SelectQualificationType
                        label={t({ id: "qualificationType" })}
                        {...input}
                        textFieldProps={{
                          error: meta.error && meta.submitFailed,
                        }}
                      />
                      {meta.error && meta.submitFailed && (
                        <FormHelperText>{meta.error}</FormHelperText>
                      )}
                    </FormControl>
                  </Grid>
                )}
              </Field>

              {submitError && (
                <Alert sx={styles.feedback} severity="error">
                  {submitError}
                </Alert>
              )}
            </Grid>
          </DialogContent>
          <DialogActions>
            {submitting && <CircularProgress size={24} />}
            <Button disabled={submitting} onClick={onCancel}>
              {t({ id: "cancelButton" })}
            </Button>
            <Button type="submit" color="primary" disabled={submitting}>
              {t({ id: "saveButton" })}
            </Button>
          </DialogActions>
        </form>
      )}
    />
  );
};

export default QualificationForm;
